import React, { useState, useEffect, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import cx from 'classnames';
import { useHistory } from 'react-router-dom';

import { useIsMount } from 'hooks/useIsMount';
import talentCloudImg from 'assets/images/MainLanding/talent-cloud.svg';
import corporateSolutionsImg from 'assets/images/MainLanding/corporate-solutions.svg';
import backofficeSolutionsImg from 'assets/images/MainLanding/back-office-solutions.svg';

enum ProductsTabKeys {
  BOSolutions,
  TalentCloud,
  CorporateSolutions,
}

interface UseStylesProps {
  rotate: number;
}

type OrbitDotProps = {
  rotationDegree: number;
};

const ROTATION_STEP = 1;
const ROTATION_INTERVAL = 12.5;
const BO_STOP_ROTATION = 39;
const TC_STOP_ROTATION = 161;
const CS_STOP_ROTATION = 293;
const CIRCLE_MAX_DEGREES = 360;

const useStyles = createUseStyles({
  orbitDot: {
    transform: ({ rotate }: UseStylesProps) => `rotate(${rotate}deg)`,
  },
});

const OrbitDot = React.memo(({ rotationDegree }: OrbitDotProps) => {
  const classes = useStyles({ rotate: rotationDegree });

  return (
    <div className={cx('orbit-for-dot', classes.orbitDot)}>
      <span className="dot" />
    </div>
  );
});

export default function OurProductsSection() {
  const [activeTab, setActiveTab] = useState<ProductsTabKeys>(
    ProductsTabKeys.BOSolutions,
  );
  const [dotRotationDegree, setDotRotationDegree] = useState(BO_STOP_ROTATION);
  const dotIntervalRef: any = useRef();
  const isMount = useIsMount();
  const history = useHistory();

  useEffect(() => {
    if (isMount) return;

    resetRotation();

    dotIntervalRef.current = setInterval(() => {
      setDotRotationDegree((rotationDeg) => {
        if (rotationDeg === CIRCLE_MAX_DEGREES - 1) return 0;

        let stopDeg;

        switch (activeTab) {
          case ProductsTabKeys.BOSolutions:
            stopDeg = BO_STOP_ROTATION;
            break;
          case ProductsTabKeys.TalentCloud:
            stopDeg = TC_STOP_ROTATION;
            break;
          case ProductsTabKeys.CorporateSolutions:
            stopDeg = CS_STOP_ROTATION;
            break;
        }

        if (rotationDeg === stopDeg) {
          resetRotation();
          return rotationDeg;
        }

        return rotationDeg + ROTATION_STEP;
      });
    }, ROTATION_INTERVAL);

    return () => resetRotation();
  }, [activeTab, isMount]);

  const resetRotation = () => {
    clearInterval(dotIntervalRef.current);
  };

  const getOrbitImage = () => {
    switch (activeTab) {
      case ProductsTabKeys.BOSolutions:
        return backofficeSolutionsImg;
      case ProductsTabKeys.TalentCloud:
        return talentCloudImg;
      case ProductsTabKeys.CorporateSolutions:
        return corporateSolutionsImg;
    }
  };

  return (
    <section className="our-products-section bg-text-section">
      <span className="bg-text">Products</span>

      <div className="wrapper section-content">
        <div className="container">
          <div className="title">
            Our <span>Products</span>
          </div>
        </div>

        <div className="d-flex">
          <div className="tab-img">
            <div className="tab-img-wrapper">
              <OrbitDot rotationDegree={dotRotationDegree} />

              <div className="orbit-for-point orbit-for-point-1">
                <div
                  onClick={() => setActiveTab(ProductsTabKeys.BOSolutions)}
                  className={cx('point point-1', {
                    active: activeTab === ProductsTabKeys.BOSolutions,
                  })}
                >
                  01
                </div>
              </div>

              <div className="orbit-for-point orbit-for-point-2">
                <div
                  onClick={() => setActiveTab(ProductsTabKeys.TalentCloud)}
                  className={cx('point point-2', {
                    active: activeTab === ProductsTabKeys.TalentCloud,
                  })}
                >
                  02
                </div>
              </div>

              <div className="orbit-for-point orbit-for-point-3">
                <div
                  onClick={() =>
                    setActiveTab(ProductsTabKeys.CorporateSolutions)
                  }
                  className={cx('point point-3', {
                    active: activeTab === ProductsTabKeys.CorporateSolutions,
                  })}
                >
                  03
                </div>
              </div>

              <div className="orbit">
                <div className="wrapper-img">
                  <img src={getOrbitImage()} alt="" />
                </div>
              </div>
            </div>
          </div>

          <div className="tab-content">
            <div className="tab">
              <button
                onClick={() => setActiveTab(ProductsTabKeys.BOSolutions)}
                className={cx('tablinks', {
                  active: activeTab === ProductsTabKeys.BOSolutions,
                })}
              >
                <span className="tab-title">
                  Back-Office
                  <br />
                  Solutions
                </span>
                <span className="tab-number">01</span>
              </button>

              <button
                onClick={() => setActiveTab(ProductsTabKeys.TalentCloud)}
                className={cx('tablinks', {
                  active: activeTab === ProductsTabKeys.TalentCloud,
                })}
              >
                <span className="tab-title">
                  TA
                  <br />
                  Marketplace
                </span>
                <span className="tab-number">02</span>
              </button>

              <button
                onClick={() => setActiveTab(ProductsTabKeys.CorporateSolutions)}
                className={cx('tablinks', {
                  active: activeTab === ProductsTabKeys.CorporateSolutions,
                })}
              >
                <span className="tab-title">
                  Corporate
                  <br />
                  TA
                </span>
                <span className="tab-number">03</span>
              </button>
            </div>

            {activeTab === ProductsTabKeys.BOSolutions && (
              <div className="tabcontent">
                <div className="content">
                  <div>
                    A trusted support system that acts as an extension of your
                    organization
                  </div>

                  <ul className="list custom-scroll">
                    <li>
                      Turnkey solutions with the urgency modern staffing
                      operations need
                    </li>

                    <li>
                      Outsource Employer of Record status to minimize liability
                    </li>

                    <li>Transformative funding to free up payroll capital</li>
                  </ul>
                </div>

                <button
                  className="btn primary-btn size-lg"
                  onClick={() => history.push('/financial-eor-compliance')}
                >
                  <span>Learn more</span>
                </button>
              </div>
            )}

            {activeTab === ProductsTabKeys.TalentCloud && (
              <div className="tabcontent">
                <div className="content">
                  <div>
                    A talent platform connecting every type of professional,
                    from employers to recruiters.
                  </div>

                  <ul className="list custom-scroll">
                    <li>Get access to exclusive job reqs</li>
                    <li>Reduce your time to hire</li>
                    <li>Enhance your network and gain market exposure</li>
                  </ul>
                </div>

                <button
                  className="btn primary-btn size-lg"
                  onClick={() => history.push('/jobs-talent-aquisition')}
                >
                  <span>Learn more</span>
                </button>
              </div>
            )}

            {activeTab === ProductsTabKeys.CorporateSolutions && (
              <div className="tabcontent">
                <div className="content">
                  <div>
                    Boost direct sourcing capabilities with a modern contingent
                    workforce solution
                  </div>

                  <ul className="list custom-scroll">
                    <li>One provider for your flexible workforce</li>
                    <li>
                      Benefits that attract top talent while enhancing your
                      corporate brand
                    </li>
                    <li>Target specialty recruiters for hard-to-fill roles</li>
                  </ul>
                </div>

                <button
                  className="btn primary-btn size-lg"
                  onClick={() => history.push('/hr-staffing-solutions')}
                >
                  <span>Learn more</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
